import type { LeadsType } from "app/pages/sales-intelligence/modules/leads-search/types";
import { createCancelable } from "app/pages/workspace/sales/helpers";
import type { IFetchService } from "app/services/fetchService";

import type {
    ExportSettingResponse,
    AddExportSettingsRequest,
    SaveExportSettingsResponse,
    ExportDomainsResponse,
    ExportType,
    SalesforcePreDefinedType,
} from "../../type";

export const createSfExportSettingsApiService = (fetchService: IFetchService) => {
    const getExportPath = (leadsType: LeadsType): string => {
        if (leadsType === "companies") {
            return "sales-api/salesforce/export/companies/accounts";
        }

        return "sales-api/salesforce/export/accounts";
    };

    const getExportAsLeadPath = (leadsType: LeadsType): string => {
        if (leadsType === "companies") {
            return "/sales-api/salesforce/export/companies/company-as-lead";
        }

        return "sales-api/salesforce/export/company-as-lead";
    };

    return {
        getSfExportSettings: createCancelable(
            (signal: AbortSignal, type: ExportType): Promise<ExportSettingResponse[]> => {
                return fetchService.get(`sales-api/salesforce/mapping-field?type=${type}`, undefined, {
                    cancellation: signal,
                });
            },
        ),
        fetchSfExportSettings(cancellation: AbortSignal) {
            return fetchService.get<ExportSettingResponse[]>(`sales-api/salesforce/mapping-field`, undefined, {
                cancellation,
            });
        },
        saveSfExportSettings: (
            settings: AddExportSettingsRequest,
            type: SalesforcePreDefinedType,
        ): Promise<SaveExportSettingsResponse> => {
            return fetchService.post(`sales-api/salesforce/mapping-field/export/${type}`, settings);
        },
        exportDomainsToSF: createCancelable(
            (
                signal: AbortSignal,
                domains: readonly string[],
                countries: readonly number[],
                leadsType: LeadsType,
                listId?: string,
            ): Promise<ExportDomainsResponse> => {
                return fetchService.post(
                    getExportPath(leadsType),
                    { domains, countries, listId },
                    {
                        cancellation: signal,
                    },
                );
            },
        ),
        exportDomainsAsLeadToSF: createCancelable(
            (
                signal: AbortSignal,
                domains: readonly string[],
                countries: readonly number[],
                leadsType: LeadsType,
            ): Promise<ExportDomainsResponse> => {
                return fetchService.post(
                    getExportAsLeadPath(leadsType),
                    { domains, countries },
                    {
                        cancellation: signal,
                    },
                );
            },
        ),
    };
};
